import { useEffect, useMemo, useState } from "react";
import { ArrowRight } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { useDoctors } from "@/hooks/useDoctors";
import { DoctorCard } from "@/components/knmc/DoctorCard";
import type { DoctorsSettings as DoctorsSettingsData } from "@/hooks/useHomeSettings";
import { shuffleArray } from "@/lib/shuffle";

export function Doctors({ settings }: { settings?: DoctorsSettingsData | null }) {
  const { doctors, isLoading } = useDoctors();

  const heading = settings?.heading || "Meet our specialists";
  const title = settings?.title || "World-class doctors, here in Kathmandu";
  const ctaLabel = settings?.cta_label || "View all doctors";
  const ctaLink = settings?.cta_link || "/doctors";

  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);

  const featured = useMemo(() => {
    const withImage = doctors.filter((d) => Boolean(d.image?.trim()));
    const withoutImage = doctors.filter((d) => !d.image?.trim());

    const orderedWithImage = mounted ? shuffleArray(withImage) : withImage;

    return [...orderedWithImage, ...withoutImage].slice(0, 4);
  }, [doctors, mounted]);

  if (isLoading) return null;

  return (
    <section className="py-24">
      <div className="mx-auto max-w-7xl px-6">
        <div className="mb-12 flex flex-wrap items-end justify-between gap-6">
          <div className="max-w-2xl">
            <p className="mb-2 text-sm font-medium uppercase tracking-wider text-secondary">
              {heading}
            </p>
            <h2 className="font-display text-3xl font-semibold text-primary md:text-5xl">
              {title}
            </h2>
          </div>
          <Link to={ctaLink}>
            <Button
              size="lg"
              className="group rounded-full bg-primary px-7 py-6 text-base font-semibold text-primary-foreground shadow-elevated transition-all duration-300 hover:scale-[1.03] hover:bg-primary/90 hover:shadow-lg"
            >
              {ctaLabel}
              <ArrowRight className="ml-2 h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Button>
          </Link>
        </div>

        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {featured.map((d) => (
            <DoctorCard key={d.practitioner_name} d={d} />
          ))}
        </div>
      </div>
    </section>
  );
}